// Stage-1 CV render MODEL (Phase 4). Pure function: DB rows -> a structured model the docx renderer
// walks. This is the FIREWALL surface — the model carries ONLY the user's authored content (titles,
// orgs, dates, bullets) plus structural section labels from the taxonomy. It composes NO prose: no
// summary/objective, no citation assembled from fields. Credentials render the user's typed line as-is.

export interface Contact { name: string; email: string; phone: string; city: string }

export interface RenderEntry {
  id: string;                  // entry id — for the HTML editor (Phase 5); the docx renderer ignores it
  // cc-11 round 18 (Sol round-17 blocker 1) — the entry's OWN TAXONOMY SECTION ID (`entries.section`), carried so a
  // client surface can tell `/api/classify` which section a bullet belongs to. It is NOT the id of the RenderSection
  // this entry ends up in: output sections are MERGED display headings with synthetic `out:<label>` ids (see below),
  // and `CvWorkspace` was sending one of those as `section`, which the classifier's section-keyed routing (the
  // research null-router, classifierBatch.ts) can never match. Like `id`, the docx/pdf renderers ignore this field.
  section: string;
  archetype: 'experience' | 'credential';
  title: string | null;        // user-entered fact (degree / role / paper title)
  organization: string | null; // user-entered fact
  dateText: string;            // derived from the user's date fields (structure, not content)
  bullets: { id: string; text: string }[]; // user-authored lines (verbatim, post-cleanup); id for the editor
}
export interface RenderSection { id: string; name: string; entries: RenderEntry[] }
export interface CvModel { contact: Contact; sections: RenderSection[] }

export interface SectionRow { id: string; name: string; sort_order: number; archetype: 'experience' | 'credential' }
export interface EntryRow {
  id: string; section: string; title: string | null; organization: string | null;
  start_date: string | null; end_date: string | null; date_text: string | null; sort_order: number;
}
export interface BulletRow { id: string; entry_id: string; text: string; sort_order: number }

// Output structure — collapse the fine 19-section authoring taxonomy into the conventional CaRMS/U-of-A CV
// sections, in BLUEPRINT ORDER (not taxonomy order). ORDERED + COMPREHENSIVE: every taxonomy id is a member of
// exactly one output section; the list order IS the render order; only populated sections are emitted. Within a
// section, members render in listed order and entries stay reverse-chronological (a deferred follow-up adds
// peer-reviewed/presentation/poster SUB-headers inside Research). Any taxonomy id missing here falls through at
// the end (never silently dropped). This is REARRANGEMENT of the user's own entries — no new content.
export const OUTPUT_SECTIONS: { label: string; members: string[] }[] = [
  { label: 'Education', members: ['education', 'postgraduate-training'] },
  { label: 'Clinical Experience', members: ['clinical-experience'] },
  { label: 'Research & Publications', members: ['research-and-scholarly-activity', 'publications', 'presentations'] },
  { label: 'Teaching Experience', members: ['teaching-experience'] },
  { label: 'Leadership, Volunteer & Community', members: ['leadership-and-administrative', 'volunteer-and-community-involvement'] },
  { label: 'Awards & Honours', members: ['awards-and-honours'] },
  { label: 'Work Experience', members: ['work-employment'] },
  { label: 'Certifications, Examinations & Licensure', members: ['medical-licensure', 'certifications', 'examinations', 'professional-development-cpd'] },
  // G1-3 — professional-memberships and languages now get their OWN conventional headers (were absorbed into
  // Certifications / Additional Information): an IMG applicant's languages are a distinct, load-bearing signal that
  // shouldn't hide inside a generic "Additional Information" catch-all. Rearrangement only — no new content.
  { label: 'Professional Memberships', members: ['professional-memberships'] },
  { label: 'Languages', members: ['languages'] },
  { label: 'Interests & Activities', members: ['extracurricular-activities-and-interests'] },
  { label: 'Additional Information', members: ['additional-information'] },
];

function entryDate(e: EntryRow): string {
  if (e.date_text && e.date_text.trim()) return e.date_text.trim();
  const s = (e.start_date ?? '').trim();
  const en = (e.end_date ?? '').trim();
  // G1-3 — repair-at-render: equal start/end (a common stored-data shape, e.g. a single-date exam saved as
  // start===end) renders as ONE value, not a same-year range. Render-time only; no migration of stored rows.
  if (s && en && s === en) return s;
  if (s && en) return `${s} – ${en}`;
  // G1 FIX — a start-only date must NOT fabricate "present": open-ended ranges are the segmenter's date_text job
  // (it puts the whole "Jun 2021 - present" string there when the user actually wrote an open end). A bare
  // start_date with no end_date is a single-dated credential (a certification, exam, membership) — render the
  // bare start, never an invented end-state the user never wrote.
  if (s) return s;
  return en;
}
// reverse-chronological key — newest first by end (or start) date / date_text string.
function dateSortKey(e: EntryRow): string {
  return (e.end_date || e.start_date || e.date_text || '').trim();
}

export function buildCvModel(contact: Contact, sections: SectionRow[], entries: EntryRow[], bullets: BulletRow[]): CvModel {
  const bulletsByEntry = new Map<string, { id: string; text: string }[]>();
  for (const b of [...bullets].sort((a, b) => a.sort_order - b.sort_order)) {
    const arr = bulletsByEntry.get(b.entry_id) ?? [];
    arr.push({ id: b.id, text: b.text });
    bulletsByEntry.set(b.entry_id, arr);
  }

  const sectionById = new Map(sections.map((s) => [s.id, s]));
  const entriesBySection = new Map<string, { re: RenderEntry; key: string }[]>();
  for (const e of entries) {
    const sec = sectionById.get(e.section);
    if (!sec) continue;
    // P4 — when an entry has no title but a named organization (a membership: the name lives in `organization`), show
    // the organization AS the heading (and clear org so it isn't a leading-comma fragment). One place → fixes the
    // review display, the PDF, and the export together.
    const re: RenderEntry = {
      id: e.id, section: e.section, archetype: sec.archetype,
      title: e.title || e.organization, organization: e.title ? e.organization : '',
      dateText: entryDate(e), bullets: bulletsByEntry.get(e.id) ?? [],
    };
    const arr = entriesBySection.get(e.section) ?? [];
    arr.push({ re, key: dateSortKey(e) });
    entriesBySection.set(e.section, arr);
  }
  // reverse-chron within each section
  const entriesFor = (id: string): RenderEntry[] =>
    [...(entriesBySection.get(id) ?? [])].sort((a, b) => b.key.localeCompare(a.key)).map((x) => x.re);

  // Emit the conventional sections in BLUEPRINT ORDER, populated-only. Within a section, members render in listed
  // order; entriesFor already returns each member reverse-chronological. (Rearrangement only — no new content.)
  const out: RenderSection[] = [];
  const mapped = new Set<string>();
  for (const sec of OUTPUT_SECTIONS) {
    for (const m of sec.members) mapped.add(m);
    const merged = sec.members.flatMap((m) => entriesFor(m));
    if (merged.length > 0) out.push({ id: `out:${sec.label}`, name: sec.label, entries: merged });
  }
  // DEFENSIVE: any taxonomy section not mapped above (e.g. a future addition) still renders — in taxonomy order,
  // at the end — so entries are NEVER silently dropped.
  for (const s of [...sections].sort((a, b) => a.sort_order - b.sort_order)) {
    if (mapped.has(s.id)) continue;
    const es = entriesFor(s.id);
    if (es.length > 0) out.push({ id: s.id, name: s.name, entries: es });
  }

  return { contact, sections: out };
}
